import { Cart, CartContents, CartLineItem } from "./cart";
import { setupCheckoutButton } from "./checkout";
import { formatCurrency } from "./currency";
import { restrictNumericInput } from "./input";

export class LineItemElement {
    containerElement: HTMLElement;
    contentsElement: HTMLElement;
    productImage: ProductImageElement;
    productDetails: ProductDetailsElement;
    productUnitPrice: HTMLSpanElement;
    productQuantity: QuantityElement;
    lineItemTotalPrice: HTMLSpanElement;
    lineItem: CartLineItem
    deleteButton: DeleteButtonElement;

    constructor(lineItem: CartLineItem) {
        this.lineItem = lineItem;

        this.containerElement = document.createElement("div");
        this.containerElement.classList.add("cart-line-item");

        this.productImage = new ProductImageElement(lineItem);

        // Other than product image, all other elements are in the contentsElement container
        this.contentsElement = document.createElement("div");
        this.contentsElement.classList.add("grid-product-contents");

        this.productDetails = new ProductDetailsElement(lineItem);

        this.productUnitPrice = document.createElement("span");

        this.productUnitPrice.innerText = formatCurrency(lineItem.variant.price.amount, lineItem.variant.price.currencyCode);
        this.productUnitPrice.classList.add("cart-pricing-element", "cart-unit-pricing-element", "grid-heading-col");

        this.productQuantity = new QuantityElement(lineItem.id);
        this.productQuantity.quantityInput.value = lineItem.quantity.toString();

        this.lineItemTotalPrice = document.createElement("span");
        this.lineItemTotalPrice.innerText = `$${lineItem.totalAmount.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        this.lineItemTotalPrice.classList.add("cart-pricing-element", "grid-heading-col");

        this.deleteButton = new DeleteButtonElement(lineItem.id);
        this.deleteButton.deleteButton.addEventListener("click", () => {
            this.productQuantity.quantityChanged.dispatchEvent(new QuantityChangedEvent(this.productQuantity, 0));
        });
    }

    removeFromDOM() {
        this.containerElement.remove();
    }

    addToDOM(parentElement: HTMLElement) {
        parentElement.appendChild(this.containerElement);
        this.productImage.addToDOM(this.containerElement);
        // Add all elements other than product image to the contentsElement
        this.containerElement.appendChild(this.contentsElement);        
        this.productDetails.addToDOM(this.contentsElement);
        this.productQuantity.addToDOM(this.contentsElement);
        this.contentsElement.appendChild(this.productUnitPrice);
        this.contentsElement.appendChild(this.lineItemTotalPrice);
        this.deleteButton.addToDOM(this.contentsElement);
    }
}

export class ProductDetailsElement {
    containerElement: HTMLElement;
    titleElement: HTMLElement;
    descriptionElement: HTMLElement;

    constructor(lineItem: CartLineItem) {
        this.containerElement = document.createElement("div");
        this.containerElement.classList.add("product-details");
        this.titleElement = document.createElement("div");
        this.titleElement.classList.add("product-title");
        this.titleElement.innerText = lineItem.productTitle;

        this.descriptionElement = document.createElement("div");
        
        // Add each selected option from the variant as a span
        for (let option of lineItem.variant.selectedOptions) {
            let optionElem = document.createElement("div");
            optionElem.innerText = `${option.name}: ${option.value}`;
            this.descriptionElement.appendChild(optionElem);
        }
        
        // Add attributes to description
        for (let attr of lineItem.attributes) {
            let attrElem = document.createElement("div");
            attrElem.innerText = `${attr.key}: ${attr.value}`;
            this.descriptionElement.appendChild(attrElem);
        }
    }
    addToDOM(parentElement: HTMLElement) {
        this.containerElement.appendChild(this.titleElement);
        this.containerElement.appendChild(this.descriptionElement);
        parentElement.appendChild(this.containerElement);
    }
    removeFromDOM() {
        this.containerElement.remove();
    }
}

export class ProductImageElement {
    containerElement: HTMLElement;
    imageElement: HTMLImageElement;


    constructor(lineItem: CartLineItem) {
        this.containerElement = document.createElement("div");
        this.containerElement.classList.add("grid-product-col");
        this.imageElement = document.createElement("img");
        this.imageElement.alt = lineItem.productTitle;
        this.imageElement.src = lineItem.featuredImage ? lineItem.featuredImage : "./images/placeholder_200x200.jpg";
        this.imageElement.classList.add("product-image");
    }
    addToDOM(parentElement: HTMLElement) {
        this.containerElement.appendChild(this.imageElement);
        parentElement.appendChild(this.containerElement);
    }
    removeFromDOM() {
        this.containerElement.remove();
    }
}

export class QuantityChangedEvent extends Event {
    quantityElement: QuantityElement;
    newQuantity: number;
    constructor(quantityElement: QuantityElement, newQuantity: number) {
        super("quantityChanged");
        this.quantityElement = quantityElement;
        this.newQuantity = newQuantity;
    }
}

export class DeleteButtonElement {
    containerElement: HTMLElement;
    deleteButton: HTMLButtonElement;
    lineItemId: string;

    constructor(lineItemId: string) {
        this.lineItemId = lineItemId;

        this.containerElement = document.createElement("div");
        this.containerElement.classList.add("delete-button-element", "grid-product-delete-col");

        this.deleteButton = document.createElement("button");
        this.deleteButton.classList.add("delete-button");
        this.deleteButton.innerText = "x";
    }

    addToDOM(parentElement: HTMLElement) {
        this.containerElement.appendChild(this.deleteButton);
        parentElement.appendChild(this.containerElement);
    }

    removeFromDOM() {
        this.containerElement.remove();
    }
}

export class QuantityElement {
    containerElement: HTMLElement;
    decrementBtn: HTMLButtonElement;
    incrementBtn: HTMLButtonElement;
    quantityInput: HTMLInputElement;
    lineItemId: string;
    quantityChanged: EventTarget;

    constructor(lineItemId: string) {
        this.lineItemId = lineItemId;
        this.quantityChanged = new EventTarget();

        this.containerElement = document.createElement("div");
        this.containerElement.classList.add("quantity-element", "grid-heading-col");

        this.decrementBtn = document.createElement("button");
        this.decrementBtn.innerText = "-";
        this.decrementBtn.classList.add("quantity-button");

        this.incrementBtn = document.createElement("button");
        this.incrementBtn.innerText = "+";
        this.incrementBtn.classList.add("quantity-button");

        this.quantityInput = document.createElement("input");
        this.quantityInput.id = `quantity-input-${this.lineItemId}`;
        this.quantityInput.type = "text";
        this.quantityInput.min = "0";
        this.quantityInput.max = "999";
        this.quantityInput.step = "1";
        this.quantityInput.autocomplete= "off";
        this.quantityInput.classList.add("quantity-input");

        // Bind to onclick of qty decrement button
        this.decrementBtn.addEventListener("click", () => {
            this.quantityChanged.dispatchEvent(new QuantityChangedEvent(this, Number(this.quantityInput.value) - 1));
        });

        // Bind to onclick of qty increment button
        this.incrementBtn.addEventListener("click", () => {
            this.quantityChanged.dispatchEvent(new QuantityChangedEvent(this, Number(this.quantityInput.value) + 1));
        });

        this.quantityInput.addEventListener("change", (e) => {
            restrictNumericInput(e as InputEvent);
            const newQuantity = Number(this.quantityInput.value);
            this.quantityChanged.dispatchEvent(new QuantityChangedEvent(this, newQuantity));
        });
    }

    addToDOM(parentElement: HTMLElement) {
        this.containerElement.appendChild(this.decrementBtn);
        this.containerElement.appendChild(this.quantityInput);
        this.containerElement.appendChild(this.incrementBtn);
        parentElement.appendChild(this.containerElement);
    }

    removeFromDOM() {
        this.containerElement.remove();
    }
}

export class CartPage {

    static lineItemIdCounter = 0;

    private lineItems: Map<string, LineItemElement> = new Map<string, LineItemElement>();

    constructor() {
    }

    async setupPage(){
        if(document.readyState === "loading") {
            throw new Error("Cannot setup cart page when document is still loading");
        }

        setupCheckoutButton();

        const cartAPI = Cart.getInstance();

        const cartId = await cartAPI.getOrCreateCart();

        const cartData = await cartAPI.getCart(cartId);

        this.populateCart(cartId, cartData);
    }

    populateCart(cartId: string, cartContents: CartContents) {

        // populate the total amount using subtotal (as it is pre-tax, pre-shipping, and pre-discount)
        const totalAmount = cartContents.subtotalAmount.amount;
        let totalElem = document.getElementById("cart-total-value");
        if(totalElem === undefined || totalElem == null) {
            throw new Error("Could not find 'cart-total-value' element");
        }
        totalElem.textContent = `$${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

        this.clearLineItems();

        // Clear the placeholder line item if we have contents in the cart
        if(cartContents.lineItems.length > 0) {
            this.clearPlaceholderEmptyCartLineItem();
        }

        // Iterate line items in the cart
        for(let lineItem of cartContents.lineItems) {
            this.addOrReplaceLineItem(cartId, lineItem);
        }
    }

    clearPlaceholderEmptyCartLineItem() {
        // We have a placeholder line item that is shown when the cart is empty
        let placeholderElem = document.getElementById("empty-cart-placeholder")
        if(placeholderElem) {
            placeholderElem.remove();
        }
    }

    clearLineItems() {
        // Iterate the line item elements map
        for(let lineItemElem of this.lineItems.values()) {
            lineItemElem.removeFromDOM();
        }

        this.lineItems.clear();
    }

    addOrReplaceLineItem(cartId: string, lineItem: CartLineItem){

        let newLineItemElem = new LineItemElement(lineItem);
        this.lineItems.set(lineItem.id, newLineItemElem);

        // Get the element with id="cart-grid"
        let lineItemsParent = document.getElementById("cart-grid");
        if(lineItemsParent === undefined || lineItemsParent == null) {
            throw new Error("Could not find 'cart-grid' element");
        }

        newLineItemElem.addToDOM(lineItemsParent);

        // Bind to the quantity changed event
        newLineItemElem.productQuantity.quantityChanged.addEventListener("quantityChanged", (e: Event) => {
            this.setQuantityElementValue(cartId, lineItem.id, newLineItemElem.productQuantity, (e as QuantityChangedEvent).newQuantity);
        });

    }

    // createLineItemElements(cartId: string, lineItem: CartLineItem) : HTMLElement {

    //     const baseId = CartPage.lineItemIdCounter++;

    //     const decrementQtyBtnId = `decrementQuantityBtn_${baseId}`;
    //     const incrementQtyBtnId = `incrementQuantityBtn_${baseId}`;
    //     const deleteBtnId = `deleteLineItemBtn_${baseId}`;
    //     const quantityInputId = `lineItemQuantityInput_${baseId}`;

    //     let lineItemHTML = `
    //         <div line-item-title="${lineItem.title}">${lineItem.title}</div>
    //         <div line-item-variant-title="${lineItem.variantTitle}">${lineItem.variantTitle}</div>
    //         ${lineItem.attributes.map((entry) => `<div line-item-attr-${entry.key}="${entry.value}">${entry.key}:${entry.value}</div>`).join("")}
    //         <div>
    //             <span>Quantity:</span>
    //             <button id="${decrementQtyBtnId}">-</button>
    //             <input id="${quantityInputId}" type="number" min="0" max="1000" step="1" value="${lineItem.quantity}"></input>
    //             <button id="${incrementQtyBtnId}">+</button>
    //             <button id="${deleteBtnId}">Delete</button>
    //         </div>
    //         <div line-item-amount="${lineItem.totalAmount.amount}" line-item-currency="${lineItem.totalAmount.currencyCode}">Subtotal: $${lineItem.totalAmount.amount} ${lineItem.totalAmount.currencyCode}</div>
    //     `;

    //     // Create div container to hold the line item elements
    //     // let lineItemContainer = document.createElement("p");
    //     // lineItemContainer.setAttribute("line-item-id", lineItem.id);
    //     // lineItemContainer.innerHTML = lineItemHTML;

    //     // Bind button click and input onchange events
    //     const decrementQtyBtn = document.getElementById(`${decrementQtyBtnId}`) as HTMLButtonElement;
    //     const incrementQtyBtn = document.getElementById(`${incrementQtyBtnId}`) as HTMLButtonElement;
    //     const deleteBtn = document.getElementById(`${deleteBtnId}`) as HTMLButtonElement;
    //     const quantityInput = document.getElementById(`${quantityInputId}`) as HTMLInputElement;

    //     // Bind to onclick of delete button
    //     deleteBtn.addEventListener("click", () => {
    //         this.setQuantityElementValue(cartId, lineItem.id, quantityInput, 0);
    //     });

    //     // Bind to onchange of quantity input
    //     quantityInput.addEventListener("change", (e) => {
    //         restrictNumericInput(e as InputEvent);

    //         this.onQuantityChanged(e, cartId, lineItem.id);
    //     });

    //     return lineItemContainer;
    // }

    setQuantityElementValue(cartId: string, lineItemId: string, quantityElement: QuantityElement, newQuantity: number) {
        if(newQuantity >= 0 && newQuantity <= 1000) {
            quantityElement.quantityInput.value = newQuantity.toString();

            // Update in the quantity in the shopify API
            (async () => {
                await this.updateQuantity(cartId, lineItemId, quantityElement, Number(newQuantity));
            })();
        }
    }

    // onQuantityChanged(e: Event, cartId: string, lineItemId: string) {

    //     if(e.target === null || e.target === undefined) {
    //         throw new Error("Quantity changed event target is null or undefined");
    //     }

    //     const inputElement = e.target as HTMLInputElement;
    //     const newQuantity = inputElement.value;

    //     (async () => {
    //         await this.updateQuantity(cartId, lineItemId, inputElement, Number(newQuantity));
    //     })();
    // }

    async updateQuantity(cartId: string, lineItemId: string, quantityElement: QuantityElement, newQuantity: Number) {
        quantityElement.quantityInput.disabled = true;

        quantityElement.incrementBtn.disabled = true;
        quantityElement.decrementBtn.disabled = true;

        let cartData = await Cart.getInstance().updateQuantityInCart(cartId, lineItemId, newQuantity);
        this.populateCart(cartId, cartData);

        quantityElement.quantityInput.disabled = false;
        quantityElement.incrementBtn.disabled = false;
        quantityElement.decrementBtn.disabled = false;

    }

}